import { Helmet } from "react-helmet-async";
import React, { useEffect, useState } from "react";
import { listCourses, createEnrollment } from "../lib/db";
import { formatSupabaseError } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useSiteImage } from "../context/SiteImagesContext";
import { Clock, User, Activity, Filter } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import CalendarioWidget from "../components/CalendarioWidget";

const CATEGORIES = ["Tutti", "Forza", "Cardio", "Mobilità", "Calcio"];

export default function Palestra() {
  const [courses, setCourses] = useState([]);
  const [filter, setFilter] = useState("Tutti");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { user } = useAuth();
  const heroImg = useSiteImage("palestra_hero", "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1600&q=80");

  useEffect(() => {
    listCourses().then(setCourses).catch(() => {});
  }, []);

  const filtered = filter === "Tutti" ? courses : courses.filter((c) => c.category === filter);

  // L'iscrizione a un corso è un form pubblico: non serve più un account.
  // Il visitatore compila nome, email, telefono e note direttamente qui.
  const openEnroll = (c) => {
    setSelected(c);
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    const f = new FormData(e.currentTarget);
    try {
      await createEnrollment(user?.user_id || null, selected, {
        full_name: f.get("full_name"),
        email: f.get("email"),
        phone: f.get("phone"),
        notes: f.get("notes") || "",
      });
      toast.success("Richiesta inviata!", { description: "Ti contatteremo per confermare l'iscrizione." });
      setOpen(false);
    } catch (err) {
      toast.error(formatSupabaseError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="palestra-page">
      <Helmet>
  <title>Palestra – ASD Rubiana Calcio</title>
  <meta name="description" content="Corsi di fitness, functional training, pilates e molto altro alla palestra dell'ASD Rubiana Calcio in Val di Susa. Orari e iscrizioni." />
  <meta property="og:title" content="Palestra – ASD Rubiana Calcio" />
  <meta property="og:description" content="Corsi di fitness e palestra in Val di Susa. Scopri orari e programmi dell'ASD Rubiana Calcio." />
  <meta property="og:url" content="https://rubianacalcio.it/palestra" />
  <meta property="og:type" content="website" />
  <meta property="og:image" content="https://rubianacalcio.it/logo512.png" />
</Helmet>
      {/* Hero */}
      <section className="relative border-b border-zinc-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="h-full w-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/70 to-zinc-950" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="text-overline text-yellow-400 mb-4">// Corsi & allenamenti</div>
          <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter uppercase leading-none">
            La palestra<br /><span className="text-yellow-400">della community</span>
          </h1>
          <p className="mt-6 max-w-2xl text-zinc-400">Molte discipline, istruttori certificati e attrezzature professionali. Trova l'allenamento giusto per te e iscriviti in pochi click.</p>
        </div>
      </section>

      {/* Filter chips */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
        <div className="flex items-center gap-3 flex-wrap" data-testid="course-filters">
          <Filter className="h-4 w-4 text-zinc-500" />
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-4 py-2 rounded-full text-xs font-display font-bold uppercase tracking-wider transition-colors ${
                filter === c ? "bg-yellow-400 text-zinc-950" : "bg-zinc-900 text-zinc-400 hover:text-zinc-100 border border-zinc-800"
              }`}
              data-testid={`filter-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* Calendario palestra */}
<section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-4">
  <div className="mb-6">
    <div className="text-overline text-yellow-400 mb-2">// Orari settimanali</div>
    <h2 className="font-display font-bold text-2xl">Quando allenarsi</h2>
  </div>
  <CalendarioWidget tipo="palestra" compact={false} />
</section>

      {/* Courses grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c) => (
            <div key={c.id} className="group rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-yellow-400/40 transition-all hover:-translate-y-1 flex flex-col" data-testid={`course-${c.id}`}>
              <div className="aspect-[16/10] overflow-hidden relative">
                <img src={c.image_url} alt={c.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-3 left-3 px-3 py-1 rounded-full bg-zinc-950/80 backdrop-blur text-overline text-yellow-400 text-[10px]">{c.category}</div>
                <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-blue-600/90 text-white text-[10px] font-bold uppercase tracking-wider">{c.level}</div>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-display font-bold text-xl mb-2">{c.name}</h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-4 flex-1">{c.description}</p>
                <div className="space-y-2 text-xs text-zinc-500 mb-5">
                  <div className="flex items-center gap-2"><User className="h-3.5 w-3.5" />{c.instructor}</div>
                  <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5" />{c.schedule} • {c.duration_min} min</div>
                </div>
                <button onClick={() => openEnroll(c)} className="w-full px-5 py-3 rounded-full bg-yellow-400 text-zinc-950 font-display font-bold hover:bg-yellow-300 active:scale-95 transition-all" data-testid={`enroll-${c.id}`}>
                  Richiedi iscrizione
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-md" data-testid="enroll-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase tracking-tight">Iscrizione: {selected?.name}</DialogTitle>
            <DialogDescription className="text-zinc-400">Compila il modulo, ti contatteremo per confermare e completare i documenti.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="full_name" className="text-zinc-300">Nome e cognome</Label>
              <Input id="full_name" name="full_name" required defaultValue={user?.name || ""} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="enroll-fullname" />
            </div>
            <div>
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <Input id="email" type="email" name="email" required defaultValue={user?.email || ""} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="enroll-email" />
            </div>
            <div>
              <Label htmlFor="phone" className="text-zinc-300">Telefono</Label>
              <Input id="phone" name="phone" required className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="enroll-phone" />
            </div>
            <div>
              <Label htmlFor="notes" className="text-zinc-300">Note (facoltative)</Label>
              <Textarea id="notes" name="notes" rows={3} className="bg-zinc-950 border-zinc-800 mt-1.5" data-testid="enroll-notes" />
            </div>
            <Button type="submit" disabled={submitting} className="w-full bg-yellow-400 text-zinc-950 hover:bg-yellow-300 font-display font-bold" data-testid="enroll-submit">
              {submitting ? "Invio…" : "Invia richiesta"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}